import type {DoppeHideableValue} from './doppe-hideable-value';
import {doppeHideableValueCreateHidden, doppeHideableValueModifyValue} from './doppe-hideable-value';
import type {CurrencyCode, OmitStrict, RichTextContent} from '@wix/devzai-utils-common';
import type {DoppeFormActionSettings} from './doppe-form-action-settings';
import {
    doppeFormActionSettingsResolveDefaultCrmType,
    getDefaultDoppeFormActionSettings
} from './doppe-form-action-settings';
import type {DoppeDtoAccountData} from './doppe-dto-account-data';
import type {DoppeCheckoutProvider} from './doppe-checkout-provider';
import {DoppeCheckoutProvider as DoppeCheckoutProviderValues} from './doppe-checkout-provider';

// never change this value, it is used to identify the default currency code
export const DefaultCurrencyCode = 'USD';

export interface DoppeCheckoutSettings extends DoppeFormActionSettings {
    // Checkout-specific UI fields
    showEmailInput: boolean;
    showAddressInput: boolean;
    showPhoneInput: boolean;
    showConsent: boolean;

    // Checkout-specific notification fields
    emailConfirmationMessage: DoppeHideableValue<RichTextContent>;
    customConfirmationMail: DoppeHideableValue<string>;
    confirmationPhone: DoppeHideableValue<string>;

    // Checkout-specific features
    sendReceipt: boolean;
    currencyCode: CurrencyCode;

    // Note: CRM fields (crmType, creatorsListId, linkedWixSiteListId, listId) 
    // and common form fields (showConsentCheckbox, customConsentText, etc.) 
    // are inherited from DoppeFormActionSettings
}

export interface DoppeCheckoutSettingsWithBindings extends OmitStrict<DoppeCheckoutSettings, 'currencyCode'>{
    title: DoppeHideableValue<string>;
}

export interface DoppeCheckoutActionSettings {
    checkoutSettings: DoppeCheckoutSettingsWithBindings;
    /**
     * Defines which checkout provider to use for payment processing.
     * Set at action creation time based on account settings and is not user-editable.
     * - Stripe: Uses Stripe Elements for in-app payment
     * - WixEcom: Redirects to Wix checkout page (only for BM integration users)
     */
    checkoutProvider?: DoppeCheckoutProvider;
}

export function getDefaultDoppeCheckoutActionSettings(overrides?: Partial<DoppeCheckoutSettings>) {
    return {
        checkoutSettings: getDefaultDoppeCheckoutSettingsForAction(overrides),
        checkoutProvider: DoppeCheckoutProviderValues.Stripe, // Default to Stripe for backwards compatibility
    } satisfies DoppeCheckoutActionSettings;
}

export function getDefaultDoppeCheckoutActionSettingsWithCrmType(
    currentAccount: DoppeDtoAccountData.WithExtendedData | undefined,
    overrides?: Partial<DoppeCheckoutSettings>
) {
    const defaultCrmType = doppeFormActionSettingsResolveDefaultCrmType(currentAccount);
    
    return {
        checkoutSettings: getDefaultDoppeCheckoutSettingsForAction({
            crmType: defaultCrmType,
            ...overrides
        }),
        checkoutProvider: DoppeCheckoutProviderValues.Stripe, // Default to Stripe for backwards compatibility
    } satisfies DoppeCheckoutActionSettings;
}

/**
 * Get default checkout action settings with the checkout provider determined by account settings.
 * New users with Wix BM integration will use WixEcom, others will use Stripe.
 */
export function getDefaultDoppeCheckoutActionSettingsWithProvider(
    options: {
        currentAccount: DoppeDtoAccountData.WithExtendedData | undefined;
        useWixCheckout: boolean;
        checkoutSettingsOverrides?: Partial<DoppeCheckoutSettings>;
    }
) {
    const {
        currentAccount,
        useWixCheckout,
        checkoutSettingsOverrides,
    } = options;
    
    const defaultCrmType = doppeFormActionSettingsResolveDefaultCrmType(currentAccount);
    
    return {
        checkoutSettings: getDefaultDoppeCheckoutSettingsForAction({
            crmType: defaultCrmType,
            ...checkoutSettingsOverrides
        }),
        checkoutProvider: useWixCheckout 
            ? DoppeCheckoutProviderValues.WixEcom 
            : DoppeCheckoutProviderValues.Stripe,
    } satisfies DoppeCheckoutActionSettings;
}

export function doppeCheckoutActionSettingsCalculateFromAccountSettings(
    checkoutSettings: DoppeCheckoutSettingsWithBindings,
    currencyCode: CurrencyCode
) {

    return {
        ...checkoutSettings,
        currencyCode: currencyCode
    } satisfies DoppeCheckoutSettings;
}

export function getDefaultDoppeCheckoutSettingsForAction(overrides?: Partial<DoppeCheckoutSettingsWithBindings>) {

    const {
        currencyCode,
        ...checkoutSettings
    } = doppeCheckoutActionSettingsGetDefaultValues(overrides)

    return {
        ...checkoutSettings,
        title: doppeHideableValueCreateHidden(''),

        ...overrides,
    } satisfies DoppeCheckoutSettingsWithBindings;
}

export function doppeCheckoutActionSettingsGetDefaultValues(overrides?: Partial<DoppeCheckoutSettings>) {
    return {
        // Include all form action defaults (CRM fields + common form fields)
        ...getDefaultDoppeFormActionSettings(),
        
        // Checkout-specific UI defaults
        showEmailInput: true,
        showAddressInput: false,
        showPhoneInput: true,
        showConsent: true,

        // Checkout-specific notification defaults
        sendReceipt: true,
        customConfirmationMail: doppeHideableValueCreateHidden(''),
        confirmationPhone: doppeHideableValueCreateHidden(''),
        emailConfirmationMessage: doppeHideableValueCreateHidden(''),

        // Checkout-specific feature defaults
        currencyCode: DefaultCurrencyCode,
        
        // Apply any overrides
        ...overrides,
    } satisfies DoppeCheckoutSettings;
}


export function prepareCheckoutSettingsForViewer (settings: DoppeCheckoutActionSettings) {
    doppeHideableValueModifyValue(settings.checkoutSettings.customEmailForNotifications, '')
    doppeHideableValueModifyValue(settings.checkoutSettings.customConfirmationMail, '')
}

